class DossierPerfumeAssistant {
  constructor() {
    // Configuration || Settings
    this.apiBaseUrl = 'https://assistant.dossier.co';
    this.chatIndex = 0;
    this.email = '';
    this.productHandles = [];

    // Classes || IDs
    this.headerMobileHideClass = 'no-header--mobile';
    this.perfumeAssistantClass = 'perfume-assistant';
    this.perfumeAssistantFormClass = 'perfume-assistant__form';
    this.buttonToggleRecommendedClass = 'button__toggle-recommended';

    // Elements || Selectors
    this.perfumeAssistantHeader = document.querySelector('.perfume-assistant__header');
    this.dossierPerfumeAssistant = document.querySelector('dossier-perfume-assistant');
    this.introStep = document.querySelector('.perfume-assistant__intro');
    this.introForm = this.introStep.querySelector(`.${this.perfumeAssistantFormClass}`);
    this.searchStep = document.querySelector('.perfume-assistant__search');
    this.searchForm = this.searchStep.querySelector(`.${this.perfumeAssistantFormClass}`);
    this.suggestionTextItems = this.searchStep.querySelectorAll('.perfume-assistant__suggestion');
    this.chatStep = document.querySelector('.perfume-assistant__chatAI');
    this.chatForm = this.chatStep.querySelector(`.${this.perfumeAssistantFormClass}`);
    this.chatbox = this.chatStep.querySelector('.perfume-assistant__chatbox');
    this.chatTemplate = document.getElementById('chat-template');
    this.productsContainer = document.querySelector('.perfume-assistant__products');
    this.perfumeSuggestions = document.querySelectorAll('.perfume-assistant__suggestions');
    this.suggestionsSlideshowContent = document.querySelector('.perfume-assistant__suggestions-content');
    this.suggestionsSlideshowNav = document.querySelector('.perfume-assistant__suggestions-nav');
    this.prevStepButton = document.querySelector('.step-action .button__prev-step');
    this.aiResults = document.querySelector('.perfume-assistant__ai-results');
    this.productsRecommended = document.querySelector('#products-recommended');

    // Ainee Popup
    this.aineePopup = document.getElementById('ainee-popup');
  }

  init() {
    this.setPerfumeAssistantStep();
    this.onEmailFormSubmit();
    this.onMessageFormSubmit();
    this.initSuggestionsSlideshow();
    this.updateContentOnScroll();
    this.navigateToPreviousStep();
    this.handleSuggestionSelection();
    this.updateProductsLocationByViewport();
    this.toggleRecommendedProducts();
    this.closeAineePopup();
  }

  setPerfumeAssistantStep() {
    document.body.dataset.perfumeAssistantStep = 1;
  }

  updatePerfumeAssistantStep(stepNumber) {
    const newStep = Math.max(1, Math.min(3, stepNumber));
    document.body.dataset.perfumeAssistantStep = newStep.toString();
  }

  onEmailFormSubmit() {
    const validateEmail = (email) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const currentStep = this.introStep;
    const nextStep = this.searchStep;
    const form = this.introForm;
    const input = form.querySelector('#email');

    const handleEmailSubmit = async () => {
      const inputValue = input.value.trim();

      if (!validateEmail(inputValue)) {
        form.classList.add('error');
        return;
      }

      this.email = inputValue;

      try {
        const response = await fetch(`${this.apiBaseUrl}/users/au/add/v2`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: this.email }),
        });

        if (!response.ok) {
          console.error('Failed to submit email:', response.statusText);
          return;
        }

        input.value = '';
        form.classList.remove('error');
        this.updatePerfumeAssistantStep(2);
      } catch (error) {
        console.error('Error submitting email:', error);
      }
    };

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleEmailSubmit();
      }
    });

    input.addEventListener('input', () => {
      form.classList.remove('error');
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      handleEmailSubmit();
    });
  }

  onMessageFormSubmit() {
    const forms = [this.searchForm, this.chatForm];

    const handleSubmittedMessage = async (form) => {
      const input = form.querySelector('#query');
      const userMessage = input.value.replace(/(\S)/, (m) => m.toUpperCase());

      if (userMessage === '') {
        form.classList.add('error');
        return;
      }

      input.value = '';
      form.classList.remove('error');

      // Handle step transition if it's on the search step
      if (form === this.searchForm && this.searchStep && this.chatStep) {
        this.updatePerfumeAssistantStep(3);
      }

      // 👤 Render the user message
      this.renderChatTemplate(userMessage);
      
      if (form === this.chatForm && this.searchStep && this.chatStep) {
        window.scrollTo(0, document.body.scrollHeight);
      }

      try {
        const messageResponse = await fetch(`${this.apiBaseUrl}/conversations/au/add-message/v2`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: this.email, message: userMessage }),
        });

        if (!messageResponse.ok) {
          console.error('Failed to submit message:', messageResponse.statusText);
          return;
        }

        const startResponse = await fetch(`${this.apiBaseUrl}/conversations/au/start/v3`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: this.email }),
        });

        if (!startResponse.ok) {
          console.error(`Error starting conversation: ${startResponse.status} ${startResponse.statusText}`);
        } else {
          const data = await startResponse.json();
          let responseHTML = data.lastAssistantMessage;

          // 🔍 Extract the product title from original markdown-style **...** (before it's replaced)
          let productTitleMatch = responseHTML.match(/\*\*(.*?)\*\*/) || responseHTML.match(/"([^"]{3,})"/);
          let productTitle = productTitleMatch ? productTitleMatch[1] : null;

          // ✅ Wrap **...** with <span class="scent-highlight"></span>
          let formattedHTML = responseHTML.replace(/"([^"]{3,})"/g, '<span class="scent-highlight">$1</span>').replace(/\*\*(.*?)\*\*/g, '<span class="scent-highlight">$1</span>');

          // ✅ Replace [text](url) or Replace plain URLs (with or without protocol)
          let firstLinkHandled = false;

          formattedHTML = formattedHTML.replace(/\[(.*?)\]\((https?:\/\/[^\s)]+)\)/g, (match, linkText, url) => {
            try {
              const urlObj = new URL(url);
              const pathParts = urlObj.pathname.split('/').filter(Boolean);
              const rawHandle = pathParts.pop() || '';
              if (!firstLinkHandled) {
                this.productHandles.push(rawHandle);
                firstLinkHandled = true;
              }
              const finalLinkText = productTitle || linkText;
              return `<a class="product-link" href="${urlObj.href}" target="_blank">${finalLinkText}</a>`;
            } catch {
              return match;
            }
          });

          function isInsideAnchorTag(string, index) {
            const lastOpen = string.lastIndexOf('<a ', index);
            if (lastOpen === -1) return false;
            const lastClose = string.lastIndexOf('</a>', index);
            return lastClose < lastOpen;
          }

          formattedHTML = formattedHTML.replace(/((https?:\/\/)?[^\s]+\/products\/[a-z0-9\-]+)/gi, (match, group1, group2, offset, string) => {
            try {
              if (isInsideAnchorTag(string, offset)) {
                return match;
              }

              const url = match.startsWith('http') ? match : 'https://' + match;
              const urlObj = new URL(url);
              const pathParts = urlObj.pathname.split('/').filter(Boolean);
              const rawHandle = pathParts.pop() || '';

              if (!firstLinkHandled) {
                this.productHandles.push(rawHandle);
                firstLinkHandled = true;
              }

              const displayText = rawHandle
                .split('-')
                .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ');

              return `<a class="product-link" href="${urlObj.href}" target="_blank">${displayText}</a>`;
            } catch {
              return match;
            }
          });


          // ✅ Replace newlines with <br> tags
          formattedHTML = formattedHTML.replace(/\n\n/g, '<br><br>').replace(/\n/g, '<br>');
          
          // ✅ Escape problematic ampersands last
          formattedHTML = formattedHTML.replace(/&(?!(?:[a-z]+|#\d+);)/gi, '&amp;');

          const currentChatEl = this.chatbox.querySelector(`.perfume-assistant__chat[data-key="${this.chatIndex - 1}"]`);

          let productHandle = null;
          if (this.productHandles.length > 0) {
            productHandle = this.productHandles[this.productHandles.length - 1];
          }
          
          // 🤖 Render the AI response
          this.renderAiResponse(productHandle, currentChatEl, formattedHTML);

          // 🛍️ Render suggested product if available
          if (productHandle) {
            const productCard = this.productsContainer.querySelector(`.productCard[data-handle="${productHandle}"]`);
            if (!productCard) {
              this.renderSuggestedProduct(currentChatEl, productHandle);
            }
          }
        }
      } catch (error) {
        console.error('Error submitting message:', error);
      }
    };

    // Attach event listeners for both forms
    forms.forEach((form) => {
      const input = form.querySelector('#query');

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          handleSubmittedMessage(form);
        }
      });

      input.addEventListener('input', () => {
        form.classList.remove('error');
      });

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        handleSubmittedMessage(form);
      });
    });
  }

  renderChatTemplate(userMessage) {
    const newChat = this.chatTemplate.content.cloneNode(true).firstElementChild;
    newChat.dataset.key = this.chatIndex++;
    this.toggleLoadingChatState(true);
    newChat.querySelector('.user-message').textContent = userMessage;
    this.chatbox.appendChild(newChat);
  }

  renderAiResponse(productHandle, chatElement, responseHTML) {
    if (productHandle) {
      chatElement.dataset.productHandle = productHandle;
    }

    const aiMessageEl = chatElement.querySelector('.ai-message');
    chatElement.querySelector('.ai-message').innerHTML = '';

    let scrollInterval;

    new Typed(aiMessageEl, {
      strings: [responseHTML],
      typeSpeed: 15,
      showCursor: false,
      contentType: 'html',
      onBegin: () => {
        scrollInterval = setInterval(() => {
          window.scrollTo(0, document.body.scrollHeight);
        }, 50);
      },
      onComplete: () => {
        clearInterval(scrollInterval);
        window.scrollTo(0, document.body.scrollHeight);
        this.toggleLoadingChatState(false);
      }
    });
  }

  toggleLoadingChatState(isLoading) {
    this.dossierPerfumeAssistant.classList.toggle('loading', isLoading);
    document.body.classList.toggle('loading-ai-message', isLoading);
    this.chatForm.querySelector('#query').readOnly = isLoading;
    this.chatForm.querySelector('button').disabled = isLoading;
  }

  renderSuggestedProduct(chatElement, productHandle) {
    // Fetching product details using the Storefront API
    let STOREFRONT_TOKEN = '4647a7eacde8f68795722e2449af688c';

    let query = `
    {
      product(handle: "${productHandle}") {
        id
        publishedAt
        availableForSale
        title
        handle
        tags
        productType
        vendor
        totalInventory
        priceRange {
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        media(first: 1) {
          edges {
            node {
              ... on MediaImage {
                image {
                  url
                  altText
                }
              }
            }
          }
        }
        variants(first: 1) {
          edges {
            node {
              id
              title
              availableForSale
              price {
                amount
                currencyCode
              }
            }
          }
        }
        metafield(namespace: "custom", key: "scent_family") {
          value
          type
        }
      }
    }`;

    fetch('/api/2025-04/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }
      return response.json();
    })
    .then(({ data }) => {
      const product = data.product;

      if (!product) {
        console.error('Product not found:', productHandle);
        return;
      }

      function parseProductData(product) {
        const variant = product.variants?.edges?.[0]?.node || {};
        const variantId = variant.id.split('/').pop() || '';
        const media = product.media?.edges?.[0]?.node?.image || {};
        const maxPrice = product.priceRange?.maxVariantPrice?.amount;

        return {
          id: product.id || '',
          variantId: variantId || '',
          title: product.title || '',
          handle: product.handle || '',
          url: `/products/${product.handle || ''}`,
          type: product.productType || '',
          vendor: product.vendor || '',
          price: parseFloat(maxPrice || '0').toString(),
          imageUrl: media.url || '',
          imageAlt: media.altText || '',
          scentFamily: product.metafield?.value || '',
          availableForSale: product.availableForSale ?? false,
          variantAvailable: variant.availableForSale ?? false
        };
      }

      // Format Product Vendor
      function formatVendorText(text) {
        let vendorProcessed = '';
        let extraLetter = "'";

        const regex = new RegExp(`^${inspiredByText}\\s*`, 'i');
        const cleanedText = text.replace(regex, '').trim();

        if (cleanedText.includes("'s")) {
          vendorProcessed = cleanedText.split("'s")[0].trim();
          extraLetter = "'s";
        } else if (cleanedText.includes("’s")) {
          vendorProcessed = cleanedText.split("’s")[0].trim();
          extraLetter = "'s";
        } else if (cleanedText.includes("'")) {
          vendorProcessed = cleanedText.split("'")[0].trim();
        } else if (cleanedText.includes("’")) {
          vendorProcessed = cleanedText.split("’")[0].trim();
        } else {
          vendorProcessed = cleanedText.trim();
          extraLetter = "'s";
        }

        const remainingText = cleanedText.slice((vendorProcessed + extraLetter).length).trim();
        return `${inspiredByText}  <br><strong>${vendorProcessed}${extraLetter}</strong> ${remainingText}`;
      }

      // Format Product Title
      function formatTitleText(text) {
        const parts = text.split(' ');
        if (parts.length > 1) {
          return parts[0] + '<br>' + parts.slice(1).join(' ');
        }
        return text;
      }

      const productData = parseProductData(product);
      const formattedTitle = formatTitleText(productData.title);
      const formattedVendor = formatVendorText(productData.vendor);

      let productActionHTML = `
        <product-form>
            <form method="post" action="/cart/add" id="quick-add-template--product-grid" accept-charset="UTF-8" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form">
              <input type="hidden" name="form_type" value="product">
              <input type="hidden" name="utf8" value="✓">
              <input type="hidden" name="id" value="${productData.variantId}">
              <input type="hidden" name="properties[_ainee]" value="true">
              <button type="submit" name="add" class="quick-add__submit button button--full-width button--primary" aria-haspopup="dialog" aria-live="polite" data-sold-out-message="true">
                <span>${productAddToCartText}</span>
                <div class="loading-overlay__spinner hidden">
                  <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                  </svg>
                </div>
              </button>
              <input type="hidden" name="product-id" value="${productData.variantId}">
              <input type="hidden" name="section-id" value="product-grid">
            </form>
          </product-form>
          `;

      if (!productData.availableForSale || !productData.variantAvailable) {
        productActionHTML = `<product-form><ds-button><button class="quick-add__submit button button--full-width button--primary js-back-in-stock" data-modal-target="#back-in-stock-modal" data-variant-id="${productData.variantId}" data-product-title="${productData.title}" data-product-vendor="${formattedVendor}"><span>${productNotifyMeText}</span><div class="loading-overlay__spinner hidden"><svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div></button></ds-button></product-form>`;
      }

      const btnCloseRecommendedHTML = '<button class="button button__toggle-recommended js-recommended" data-target="#product-recommended"><svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none"><path d="M18.5034 7.14788L11.5067 15.0437L4.51003 7.14788" stroke-width="1.83376" stroke-linecap="round"/></svg></button>';

       // !BF - Update Pricing
      const regularPrice = productData.price;
      const discountedPrice = (regularPrice * 0.55).toFixed(2);
      // !End of: BF - Update Pricing

      let productCardHtml = `
      <div id="productCard__${productData.handle}" class="productCard active" data-id="${productData.id}" data-variant-id="${productData.variantId}" data-handle="${productData.handle}" data-title="${productData.title}" data-type="${productData.type}" data-scent-family="${productData.scentFamily}">
        <div class="productCard__container">
          <div class="productCard__image">
            <a href="${productData.url}" class="productCard__image-link" target="_blank">
              <img src="${productData.imageUrl}" alt="${productData.imageAlt}" class="productCard__image-img" width="326" height="326">
            </a>
          </div>
          <div class="productCard__content">
            <a href="${productData.url}" class="productCard__link" target="_blank">
              <h3 class="productCard__title">${formattedTitle}</h3>
              <div class="productCard__price">

                <!------------------- !!BF - Update Pricing ------------------->
                <div class="bf-prices">
                  <div class="bf-price__discount">
                   <modal-opener class="no-js-hidden open-dialog-modal open-bmsm-popup" data-modal-target="#benefits-modal"><button></button></modal-opener>
                    <div class="content">
                      <span class="price-start">-45%</span>
                      <strong class="price-amount">A$${discountedPrice}</strong>
                    </div>
                  </div>
                  <div class="bf-price__regular">
                    A$${regularPrice}
                  </div>
                </div>
                <!------------------- !!End of: BF - Update Pricing ------------------->

              </div>
              <div class="productCard__vendor">${formattedVendor}</div>
            </a>
            <div class="productCard__actions">${productActionHTML}${btnCloseRecommendedHTML}</div>
          </div>
        </div>
      </div>
      `;

      // Remove the active class from the last Product Card
      const productCards = this.productsContainer.querySelectorAll('.productCard');
      if (productCards.length > 0) {
        productCards[productCards.length - 1].classList.remove('active');
      }

      this.chatStep.dataset.productsCount = this.productHandles.length;

      // Insert the new product card HTML
      this.productsContainer.insertAdjacentHTML('beforeend', productCardHtml);

      // Update perfume assistant container with scent family class
      let scentFamily = productData.scentFamily;
      if (scentFamily || scentFamily.length > 0) {
        scentFamily = scentFamily.toLowerCase();
        chatElement.dataset.scentFamily = scentFamily;
        this.updateScentFamilyBodyClass(scentFamily);
      } else {
        this.removeScentFamilyBodyClass();
      }

      // Show the recommended products section
      if(window.innerWidth <= 900) {
        this.productsRecommended.classList.add('active');
        this.chatStep.classList.add('show-recommended');
      }
    })
    .catch(error => {
      console.error('Fetch error:', error);
    });
  }

  removeScentFamilyBodyClass() {
    this.dossierPerfumeAssistant.classList.forEach(className => {
      if (className.startsWith("scent-family__")) {
        this.dossierPerfumeAssistant.classList.remove(className);
      }
    });
  }

  updateScentFamilyBodyClass(scentFamily) {
    this.dossierPerfumeAssistant.classList.forEach(className => {
      if (className.startsWith('scent-family__')) {
        this.dossierPerfumeAssistant.classList.remove(className);
      }
    });

    this.dossierPerfumeAssistant.classList.add(`scent-family__${scentFamily}`);
  }

  initSuggestionsSlideshow() {
    if (!this.suggestionsSlideshowContent || !this.suggestionsSlideshowNav) return;

    const scrollContainer = this.suggestionsSlideshowContent;
    const scrollButton = this.suggestionsSlideshowNav.querySelector('.slider-next');

    if (!scrollButton) return;

    scrollButton.addEventListener('click', () => {
      const scrollAmount = scrollContainer.clientWidth;
      const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      const currentScrollLeft = scrollContainer.scrollLeft;

      if (currentScrollLeft >= maxScrollLeft - 1) {
        scrollContainer.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        const targetScrollLeft = Math.min(currentScrollLeft + scrollAmount, maxScrollLeft);
        scrollContainer.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
      }
    });
  }

  updateContentOnScroll() {
    let currentProductHandle = null;

    const getCenteredChatHandle = (chats) => {
      const viewportCenter = window.innerHeight / 2;

      let closestChat = null;
      let closestDistance = Infinity;

      chats.forEach(chat => {
        const rect = chat.getBoundingClientRect();
        const chatCenter = rect.top + rect.height / 2;
        const distance = Math.abs(chatCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestChat = chat;
        }
      });

      return closestChat ? closestChat.dataset.productHandle : null;
    };

    window.addEventListener('scroll', () => {
      let chats = document.querySelectorAll('.perfume-assistant__chat');
      if(chats && chats.length === 0) return;

      const newHandle = getCenteredChatHandle(chats);
      if (!newHandle) return;

      if (newHandle !== null && newHandle !== currentProductHandle) {
        currentProductHandle = newHandle;
        const activeProductCard = this.productsContainer.querySelector(`.productCard[data-handle="${currentProductHandle}"]`);
        if (activeProductCard && !activeProductCard.classList.contains('active')) {
          document.querySelector('.productCard.active')?.classList.remove('active');
          this.productsContainer.appendChild(activeProductCard);
          activeProductCard.classList.add('active');

          let scentFamily = activeProductCard.dataset.scentFamily;
          if(scentFamily && scentFamily.length > 0) {
            scentFamily = scentFamily.toLowerCase();
            this.updateScentFamilyBodyClass(scentFamily);
          } else {
            this.removeScentFamilyBodyClass();
          }
        }
      }
    });
  }

  navigateToPreviousStep() {
    if (!this.prevStepButton) return;
    this.prevStepButton.addEventListener('click', (event) => {
      event.preventDefault();
      const currentStep = parseInt(document.body.dataset.perfumeAssistantStep) || 1;
      if (currentStep <= 1) return;

      if (currentStep > 1) {
        if (currentStep === 3) {
          this.productsContainer.innerHTML = '';
          this.chatbox.innerHTML = '';
          this.chatStep.dataset.productsCount = '0';
          this.removeScentFamilyBodyClass();
          this.updatePerfumeAssistantStep(2);
        } else {
          this.updatePerfumeAssistantStep(1);
        }
      }
    });
  }

  handleSuggestionSelection() {
    this.perfumeSuggestions.forEach(suggestionsContainer => { 
      const suggestions = suggestionsContainer.querySelectorAll('.perfume-assistant__suggestion');

      suggestions.forEach(suggestion => {
        // Do something with each suggestion inside this container
        suggestion.addEventListener('click', () => {
          const suggestionText = suggestion.textContent.trim();
          const stepForm = suggestion.closest(`.${this.perfumeAssistantClass}`).querySelector(`.${this.perfumeAssistantFormClass}`);
          if (!stepForm) return;
          const input = stepForm.querySelector('input[type="text"]');
          if (!input) return;
          input.value = suggestionText.replace(/(\S)/, (m) => m.toUpperCase());
          stepForm.dispatchEvent(new Event('submit', { bubbles: true }));

          // If is on step 3
          const currentStep = parseInt(document.body.dataset.perfumeAssistantStep) || 1;
          if (currentStep === 3) {
            window.scrollTo(0, document.body.scrollHeight);
          }
        });
      });
    });
  }

  updateProductsLocationByViewport() {
    const moveProductsContainer = () => {
      if (!this.productsContainer || !this.aiResults || ! this.productsRecommended) return;

      const currentParent = this.productsContainer.parentElement;

      if (window.innerWidth <= 900 && currentParent !== this.productsRecommended) {
        this.productsRecommended.prepend(this.productsContainer);
      } else if (window.innerWidth > 900 && currentParent !== this.aiResults) {
        this.aiResults.prepend(this.productsContainer);
      }
    };

    moveProductsContainer();
    window.addEventListener('resize', moveProductsContainer);
  }

  toggleRecommendedProducts() {
    document.addEventListener('click', (e) => {
      const recommendedBtn = e.target.closest(`.${this.buttonToggleRecommendedClass}`);
      if (recommendedBtn && this.chatStep) {
        this.productsRecommended.classList.toggle('active');
        this.chatStep.classList.toggle('show-recommended');
      }
    });
  }

  closeAineePopup() {
    this.aineePopup.addEventListener('click', (event) => {
      const modalContent = this.aineePopup.querySelector('.ds-modal__content');
      const insideContent = modalContent.contains(event.target);
      const showPopup = this.aineePopup.classList.contains('show');

      if (showPopup && !insideContent) {
        this.aineePopup.classList.remove('show');
      }
    });
  }
}

const dossierPerfumeAssistant = new DossierPerfumeAssistant();
dossierPerfumeAssistant.init();